import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { StyleSheet } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

import { useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { api } from '@ppwr/convex/_generated/api'

type ScheduleForUser = FunctionReturnType<typeof api.gameSchedules.queries.getSchedulesForUser>[number]
import { useRouter } from 'expo-router'

import { DS } from '../../../../design-system'
import { useStore } from '../../../../models'
import { events } from '../../../../models/analytics/analytics-events'
import { GameModeTag, UserGameScheduleResponseModelType } from '../../../../models/api-store'
import { GameActions } from '../../GameActions'
import { GamesEmptyMessage } from './GamesEmptyMessage'
import { YourGameCard } from './YourGameCard'
import { usePollingQuery } from '@/hooks/usePollingQuery'
import { isMaintenanceModeEnabled, showMaintenanceModeAlert } from '@/screens/play-screen/maintenanceMode'

export const YourGames = observer(() => {
  const router = useRouter()
  const {
    game: {
      yourLiveGames,
      yourScheduledGames,
      yourGamesAreEmpty,
      getYourLiveGames,
      getYourScheduledGames,
      joinLiveGame,
      setUserGameScheduleFullResponse
    },
    session: { user, isTeacher }
  } = useStore()

  const userId = user?.auth0Id ?? ''
  // The 15-min join-window expiry is enforced server-side in getSchedulesForUser,
  // so passed schedules no longer need to be filtered here.
  const convexScheduledGames: ScheduleForUser[] | undefined = useQuery(
    api.gameSchedules.queries.getSchedulesForUser,
    userId ? { userId } : 'skip'
  ) as ScheduleForUser[] | undefined

  const hasConvexScheduled = (convexScheduledGames?.length ?? 0) > 0

  const joinGame = async ({ channelName, isFull, isLocked, hash, hasLeftGame }: UserGameScheduleResponseModelType) => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    if (channelName && user && isTeacher && !isLocked && !isFull && !hasLeftGame) {
      router.navigate(`/game/create/user-role?code=${hash}`)
      return
    }
    if (channelName && user) {
      await joinLiveGame(channelName)
      router.navigate('/game')
      return
    }
  }

  const navigateToGameDetails = async (userGameSchedule: UserGameScheduleResponseModelType) => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    const clonedUserGameSchedule = cloneDeep(userGameSchedule)
    setUserGameScheduleFullResponse(clonedUserGameSchedule)

    if (userGameSchedule.isHost) {
      router.navigate('/game/create/schedule-confirm?shouldManage=true')
    } else {
      router.navigate(`/game/join/accept-invite/${userGameSchedule.hash}`)
    }
  }

  const navigateToConvexSchedule = (hash: string, isHost: boolean) => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    if (isHost) {
      router.navigate(`/game/create/scheduled-tournament-confirm?hash=${hash}`)
    } else {
      router.navigate(`/game/join/accept-scheduled-invite/${hash}`)
    }
  }

  usePollingQuery('getYourLiveGames', getYourLiveGames, {
    refetchInterval: 5_000
  })

  usePollingQuery('getYourScheduledGames', getYourScheduledGames, {
    refetchInterval: 5_000
  })

  const totalScheduledCount = yourScheduledGames.length + (convexScheduledGames?.length ?? 0)
  const isEmpty = yourGamesAreEmpty && !hasConvexScheduled

  return (
    <DS.Frame gap="12" style={{ marginHorizontal: -DS.spacing[24] }}>
      <DS.Text style={styles.containerPadding} variant="Heading 3/Medium">
        Your Scheduled Games
      </DS.Text>
      {isEmpty ? (
        <DS.Frame style={styles.containerPadding}>
          <GamesEmptyMessage />
        </DS.Frame>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.containerPadding}>
          <DS.Frame row gap="8">
            {yourLiveGames.map((game, i) => (
              <YourGameCard
                context="legacy"
                key={`your-live-game-${game.name}-${i}`}
                tableName={game.name}
                blinds={game.blinds || []}
                seats={game.seats || []}
                iAmHost={game.isHost}
                isFull={game.isFull}
                hasLeftGame={game.hasLeftGame}
                isLocked={game.isLocked}
                type="live"
                pattern={i}
                onPress={debounce(() => game.channelName && joinGame(game), 250)}
              />
            ))}
            {yourScheduledGames.map((game, j) => (
              <YourGameCard
                context="legacy"
                key={`your-schedule-game-${game.name}-${j}`}
                tableName={game.name}
                blinds={game.blinds || []}
                seats={game.seats || []}
                isLocked={game.isLocked}
                hasLeftGame={game.hasLeftGame}
                iAmHost={game.isHost}
                isFull={game.isFull}
                pattern={j + yourLiveGames.length}
                scheduledAt={new Date(game.scheduleTime)}
                onPress={debounce(() => navigateToGameDetails(game), 250)}
              />
            ))}
            {convexScheduledGames?.map((schedule, k) => {
              const scheduleHash = 'hash' in schedule ? schedule.hash : undefined
              return (
                <YourGameCard
                  context="convex"
                  key={`your-convex-schedule-${schedule._id}`}
                  tableName={schedule.name}
                  blinds={[]}
                  seats={schedule.seats}
                  gameMode={
                    schedule.scheduleKind === 'community'
                      ? 'community'
                      : ('mode' in schedule ? schedule.mode : undefined) || 'private'
                  }
                  isLocked={false}
                  hasLeftGame={false}
                  iAmHost={schedule.isHost}
                  isFull={schedule.isFull}
                  type={schedule.cardType}
                  pattern={yourLiveGames.length + totalScheduledCount + k}
                  scheduledAt={new Date(schedule.scheduledStartTime)}
                  onPress={
                    schedule.scheduleKind === 'community' || !scheduleHash
                      ? undefined
                      : debounce(() => navigateToConvexSchedule(scheduleHash, schedule.isHost), 250)
                  }
                />
              )
            })}
          </DS.Frame>
        </ScrollView>
      )}
    </DS.Frame>
  )
})

export const CreateGameButton = observer(() => {
  const router = useRouter()
  const {
    analytics: { setEvent },
    game: { setGameKind, setTableName, clearWalkthrough },
    session: { user }
  } = useStore()

  const onPress = useCallback(() => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    clearWalkthrough()
    setTableName('')
    setGameKind(GameModeTag.default)
    setEvent(events.homescreen.create_game, {
      userId: user?._id.toString()
    })
    router.navigate('/game/create/new')
  }, [user, setEvent, setGameKind, setTableName, clearWalkthrough, router.navigate])

  return (
    <GameActions
      leftIcon={<DS.Icon name="PlayPokerMenuCreateGame" size={32} color={'Global/White'} />}
      title={'Create Game'}
      rightIcon={<DS.Icon name="Arrow---right" size={20} color="Global/Black" />}
      leftIconBg={'Brand/Purple'}
      onSelectingOption={debounce(onPress, 250)}
    />
  )
})

export const JoinGameButton = observer(() => {
  const router = useRouter()
  const {
    analytics: { setEvent },
    session: { user },
    game: { setTableName }
  } = useStore()

  const onPress = useCallback(() => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    setTableName('')

    setEvent(events.homescreen.join_game, {
      userId: user?._id.toString()
    })
    router.navigate('/game/join')
  }, [user, setEvent, setTableName, router.navigate])

  return (
    <GameActions
      leftIcon={<DS.Icon name="PlayPokerMenuJoinGame" size={32} color={'Global/White'} />}
      title={'Join Game'}
      rightIcon={<DS.Icon name="Arrow---right" size={20} color="Global/Black" />}
      leftIconBg={'Brand/Dark Grey'}
      onSelectingOption={debounce(onPress, 250)}
    />
  )
})

export const PracticeGameButton = observer(() => {
  const router = useRouter()
  const {
    analytics: { setEvent },
    game: { clearWalkthrough },
    session: { user }
  } = useStore()

  const onPress = useCallback(() => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    clearWalkthrough()
    setEvent(events.homescreen.practice_game, {
      userId: user?._id.toString()
    })

    router.navigate('/game/new')
  }, [user, setEvent, clearWalkthrough, router.navigate])

  return (
    <GameActions
      leftIcon={<DS.Icon name="PlayPokerMenuPractice" size={32} color={'Global/White'} />}
      title={'Practice'}
      rightIcon={<DS.Icon name="Arrow---right" size={20} color="Global/Black" />}
      leftIconBg={'Brand/Yellow'}
      onSelectingOption={debounce(onPress, 250)}
    />
  )
})

const styles = StyleSheet.create({
  containerPadding: { paddingHorizontal: DS.spacing[24] }
})
